import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Apple, Crown, Users, Info, Gift, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * iOS-only In-App Purchase block.
 *
 * Renders the same VIP tier pricing as SubscriptionPage, but instead of
 * starting a Stripe checkout, it triggers the native StoreKit bridge
 * via `window.WTN.iapPurchase(productId)` (WebToNative) OR
 * `window.webkit.messageHandlers.iap.postMessage({...})` (custom WKWebView).
 *
 * Apple App Store guideline 3.1.1 requires that any paid digital content
 * accessible inside the iOS app is also purchasable through Apple IAP.
 * This component is the user-facing IAP entry point.
 *
 * App Store Connect product IDs (must match exactly):
 *   - cca_vip_individual_monthly   ($1.99 / mo)
 *   - cca_vip_family_monthly       ($2.99 / mo)
 *   - cca_vip_cookout_monthly      ($5.99 / mo)
 *   - cca_vip_reunion_monthly      ($12.99 / mo)
 */

const IAP_TIERS = [
  {
    id: 'cca_vip_individual_monthly',
    name: 'Individual',
    nameEs: 'Individual',
    price: 1.99,
    maxUsers: 1,
    description: 'Perfect for personal use',
    descriptionEs: 'Perfecto para uso personal',
    icon: Crown,
  },
  {
    id: 'cca_vip_family_monthly',
    name: 'Family & Friends',
    nameEs: 'Familia y Amigos',
    price: 2.99,
    maxUsers: 4,
    description: 'Share with up to 4 users',
    descriptionEs: 'Comparte con hasta 4 usuarios',
    icon: Users,
    popular: true,
  },
  {
    id: 'cca_vip_cookout_monthly',
    name: 'The Whole Cookout',
    nameEs: 'Todo el Asado',
    price: 5.99,
    maxUsers: 8,
    description: 'Share with up to 8 users',
    descriptionEs: 'Comparte con hasta 8 usuarios',
    icon: Users,
  },
  {
    id: 'cca_vip_reunion_monthly',
    name: 'Family Reunion',
    nameEs: 'Reunión Familiar',
    price: 12.99,
    maxUsers: 20,
    description: 'Share with up to 20 users',
    descriptionEs: 'Comparte con hasta 20 usuarios',
    icon: Users,
  },
];

/**
 * Detect whether a native IAP bridge is exposed by the host iOS app.
 * Returns one of: 'wtn' | 'webkit' | null.
 */
function detectIapBridge() {
  if (typeof window === 'undefined') return null;
  if (window.WTN && typeof window.WTN.iapPurchase === 'function') return 'wtn';
  if (
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.iap &&
    typeof window.webkit.messageHandlers.iap.postMessage === 'function'
  ) {
    return 'webkit';
  }
  return null;
}

export default function IapPurchaseBlock({ isSpanish = false, userId = null }) {
  const [busyTier, setBusyTier] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const bridge = detectIapBridge();

  const handleRestore = () => {
    // Apple guideline 3.1.1 requires a Restore Purchases entry point
    // visible to *non*-subscribers (in case they reinstalled or switched
    // device). The native StoreKit wrapper does the actual restore and
    // delivers each restored transaction back via window.onIapResult
    // with status='restored' (or 'no_purchases' if there's nothing).
    setRestoring(true);
    try {
      if (bridge === 'wtn' && typeof window.WTN.iapRestore === 'function') {
        window.WTN.iapRestore();
      } else if (bridge === 'webkit') {
        window.webkit.messageHandlers.iap.postMessage({
          action: 'restore',
          userId: userId || null,
        });
      } else {
        toast.info(
          isSpanish
            ? 'Actualiza la app de iOS desde la App Store para restaurar tus compras.'
            : 'Please update the iOS app from the App Store to restore purchases.'
        );
        setRestoring(false);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[IAP] restore trigger failed', e);
      toast.error(
        isSpanish
          ? 'No se pudieron restaurar las compras. Intenta de nuevo.'
          : 'Could not restore purchases. Please try again.'
      );
      setRestoring(false);
    }
  };

  const handlePurchase = (productId) => {
    setBusyTier(productId);
    try {
      if (bridge === 'wtn') {
        // WebToNative wrapper. Returns void; result is delivered to
        // window.onIapResult({ productId, transactionId, receipt, status }).
        window.WTN.iapPurchase(productId);
      } else if (bridge === 'webkit') {
        // Custom WKWebView bridge. Native side resolves via the same
        // window.onIapResult callback.
        window.webkit.messageHandlers.iap.postMessage({
          action: 'purchase',
          productId,
          userId: userId || null,
        });
      } else {
        toast.info(
          isSpanish
            ? 'Actualiza la app a la versión más reciente para suscribirte con Apple In-App Purchase.'
            : 'Please update the app to the latest version to subscribe via Apple In-App Purchase.'
        );
        setBusyTier(null);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[IAP] purchase trigger failed', e);
      toast.error(
        isSpanish
          ? 'No se pudo iniciar la compra. Intenta de nuevo.'
          : 'Could not start the purchase. Please try again.'
      );
      setBusyTier(null);
    }
  };

  // Native side calls window.onIapResult to finalise the purchase.
  // We register the global handler once per mount.
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = async (payload) => {
      setBusyTier(null);
      setRestoring(false);
      try {
        const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
        if (!data || data.status === 'cancelled') return;

        // 'restored' is sent once per restored transaction; the native
        // side may emit several in a row. We treat each like a success
        // and re-link to the user's account. The final reload happens
        // after a short debounce so we only refresh the SPA once.
        if (data.status === 'no_purchases') {
          toast.info(
            isSpanish
              ? 'No se encontraron compras anteriores en este Apple ID.'
              : 'No previous purchases found on this Apple ID.'
          );
          return;
        }

        const isRestore = data.status === 'restored';
        if ((data.status === 'success' || isRestore) && (data.transactionId || data.receipt)) {
          // Forward to backend for entitlement verification. Uses the
          // existing /api/iap/apple/link route which upserts an
          // iap_links record and flips the user's subscription_status
          // to "active" optimistically (Apple's ASN V2 webhook is the
          // ground truth and will reconcile shortly after).
          try {
            const { apiFetch } = await import('../hooks/useFetch');
            await apiFetch('/api/iap/link/apple', {
              method: 'POST',
              body: {
                original_transaction_id:
                  data.originalTransactionId || data.transactionId,
                product_id: data.productId,
              },
            });
          } catch (_) {
            /* backend will also receive via S2S notification */
          }
          toast.success(
            isRestore
              ? isSpanish
                ? '¡Compras restauradas! Tu VIP está activo.'
                : 'Purchases restored! Your VIP is active.'
              : isSpanish
              ? '¡Suscripción VIP activada!'
              : 'VIP subscription activated!'
          );
          setTimeout(() => window.location.reload(), 1200);
        } else {
          toast.error(
            isSpanish
              ? `La compra no se completó: ${data.error || 'desconocido'}`
              : `Purchase did not complete: ${data.error || 'unknown'}`
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[IAP] onIapResult parsing failed', e);
      }
    };
    window.onIapResult = handler;
    return () => {
      if (window.onIapResult === handler) {
        window.onIapResult = undefined;
      }
    };
  }, [isSpanish]);

  return (
    <div className="space-y-4 mb-8" data-testid="iap-purchase-block">
      <Alert
        className="border-green-500 bg-green-50"
        data-testid="iap-intro-offer-notice"
      >
        <Gift className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-sm text-green-800">
          <strong>{isSpanish ? '¡7 Días Gratis!' : '7 Days Free!'}</strong>{' '}
          {isSpanish
            ? 'Comienza tu prueba gratuita de 7 días — cancela en cualquier momento desde Ajustes de iOS antes de que termine y no se te cobrará.'
            : 'Start your 7-day free trial — cancel anytime in iOS Settings before it ends and you won\'t be charged.'}
        </AlertDescription>
      </Alert>

      <Alert className="bg-slate-50 border-slate-300">
        <Apple className="h-4 w-4" />
        <AlertDescription className="text-sm text-slate-700">
          <strong>
            {isSpanish ? 'Compra dentro de la app' : 'In-App Purchase'}
          </strong>{' '}
          —{' '}
          {isSpanish
            ? 'Las suscripciones se procesan de forma segura por Apple. Se cobrarán a tu Apple ID al final del período de prueba.'
            : 'Subscriptions are securely processed by Apple. Your Apple ID is charged at the end of the trial period.'}
        </AlertDescription>
      </Alert>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {IAP_TIERS.map((tier) => {
          const TierIcon = tier.icon;
          return (
            <Card
              key={tier.id}
              className={`relative ${tier.popular ? 'border-primary border-2' : ''}`}
              data-testid={`iap-tier-${tier.id}`}
            >
              {tier.popular && (
                <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary">
                  {isSpanish ? 'Popular' : 'Popular'}
                </Badge>
              )}
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TierIcon className="h-5 w-5 text-yellow-500" />
                  {isSpanish ? tier.nameEs : tier.name}
                </CardTitle>
                <CardDescription className="text-xs">
                  {isSpanish ? tier.descriptionEs : tier.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-2">
                <div className="mb-2">
                  <span className="text-2xl font-bold">${tier.price}</span>
                  <span className="text-muted-foreground text-sm">
                    /{isSpanish ? 'mes' : 'mo'}
                  </span>
                </div>
                <p
                  className="text-xs font-medium text-green-700 mb-1"
                  data-testid={`iap-tier-trial-${tier.id}`}
                >
                  {isSpanish ? '7 días gratis' : '7-day free trial'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {tier.maxUsers === 1
                    ? isSpanish
                      ? '1 usuario'
                      : '1 user'
                    : isSpanish
                    ? `Hasta ${tier.maxUsers} usuarios`
                    : `Up to ${tier.maxUsers} users`}
                </p>
                {tier.maxUsers > 1 && (
                  <p
                    className="text-[11px] text-blue-700 mt-1 flex items-center gap-1"
                    data-testid={`iap-tier-family-sharing-${tier.id}`}
                  >
                    <Users className="h-3 w-3" aria-hidden="true" />
                    {isSpanish
                      ? 'Compatible con Compartir en Familia'
                      : 'Family Sharing supported'}
                  </p>
                )}
              </CardContent>
              <CardFooter>
                <Button
                  className="w-full bg-black hover:bg-zinc-800 text-white"
                  size="sm"
                  onClick={() => handlePurchase(tier.id)}
                  disabled={busyTier === tier.id}
                  data-testid={`iap-buy-${tier.id}`}
                >
                  <Apple className="h-4 w-4 mr-2" />
                  {busyTier === tier.id
                    ? isSpanish
                      ? 'Procesando…'
                      : 'Processing…'
                    : isSpanish
                    ? 'Suscríbete con Apple'
                    : 'Subscribe with Apple'}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Restore Purchases — required by Apple guideline 3.1.1 so users
          who reinstalled, switched device, or share a Family Plan can
          re-link their existing subscription. */}
      <div
        className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-center pt-1"
        data-testid="iap-restore-section"
      >
        <p className="text-xs text-muted-foreground">
          {isSpanish
            ? '¿Ya te suscribiste en otro dispositivo?'
            : 'Already subscribed on another device?'}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRestore}
          disabled={restoring}
          className="h-8 text-xs"
          data-testid="iap-restore-purchases-btn"
        >
          <RefreshCw
            className={`h-3 w-3 mr-1.5 ${restoring ? 'animate-spin' : ''}`}
            aria-hidden="true"
          />
          {restoring
            ? isSpanish
              ? 'Restaurando…'
              : 'Restoring…'
            : isSpanish
            ? 'Restaurar compras'
            : 'Restore Purchases'}
        </Button>
      </div>

      {!bridge && (
        <Alert className="border-amber-300 bg-amber-50">
          <Info className="h-4 w-4 text-amber-700" />
          <AlertDescription
            className="text-sm text-amber-800"
            data-testid="iap-bridge-missing-notice"
          >
            {isSpanish
              ? 'Si los botones no responden, actualiza la app de iOS desde la App Store para habilitar las compras dentro de la app.'
              : "If the buttons don't respond, please update the iOS app from the App Store to enable in-app purchases."}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
